import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { useTheme } from "@mui/material/styles";
import { Button, Paper, Tooltip as MuiTooltip, Typography } from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import PeriodSelect from "../../components/PeriodSelect";
import { sentimentInfo, SATISFIED_LEVELS } from "../../helpers/callFormatting";
import { rangoDePeriodo } from "../../helpers/rangoDePeriodo";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const NO_SPECIALIST_KEY = "__no_specialist__";

const buildPeriodOptions = () => [
  { valor: "hoy", etiqueta: i18n.t("callMetrics.periods.today") },
  { valor: "ayer", etiqueta: i18n.t("callMetrics.periods.yesterday") },
  { valor: "semana", etiqueta: i18n.t("callMetrics.periods.thisWeek") },
  { valor: "semanaPasada", etiqueta: i18n.t("callMetrics.periods.lastWeek") },
  { valor: "mes", etiqueta: i18n.t("callMetrics.periods.thisMonth") },
  { valor: "mesPasado", etiqueta: i18n.t("callMetrics.periods.lastMonth") },
  { valor: "personalizado", etiqueta: i18n.t("callMetrics.periods.custom") },
];

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2.5),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  dateFilterControl: {
    display: "flex",
    alignItems: "stretch",
    height: 34,
    border: `1px solid ${theme.palette.mode === "light" ? "#c3c2b7" : "#383835"}`,
    borderRadius: 8,
    overflow: "hidden",
    flexShrink: 0,
  },
  dateFilterInput: {
    border: "none",
    outline: "none",
    background: "transparent",
    color: theme.palette.text.primary,
    fontSize: 13,
    fontFamily: theme.typography.fontFamily,
    padding: theme.spacing(0, 1.25),
    borderRight: `1px solid ${theme.palette.mode === "light" ? "#c3c2b7" : "#383835"}`,
    colorScheme: theme.palette.mode === "light" ? "light" : "dark",
  },
  periodSelectPill: {
    fontSize: 13,
    color: theme.palette.text.primary,
    padding: theme.spacing(0, 1.25),
    borderRight: `1px solid ${theme.palette.mode === "light" ? "#c3c2b7" : "#383835"}`,
    display: "flex",
    alignItems: "center",
  },
  dateFilterButton: {
    borderRadius: 0,
    boxShadow: "none",
    height: "100%",
    fontSize: 12.5,
    fontWeight: 600,
    padding: theme.spacing(0, 2),
    minWidth: 0,
    whiteSpace: "nowrap",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  tile: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
  },
  num: {
    fontSize: "1.4rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  infoIcon: {
    fontSize: 13,
    cursor: "help",
    color: theme.palette.text.disabled,
  },
  tileHint: {
    fontSize: "0.68rem",
    color: theme.palette.text.disabled,
    marginTop: 2,
  },
  chartsRowFull: {
    marginBottom: theme.spacing(3),
  },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  chartCard: {
    position: "relative",
    padding: theme.spacing(2.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    height: 340,
    display: "flex",
    flexDirection: "column",
  },
  chartCardTall: {
    height: 300,
  },
  sectionTitle: {
    fontSize: "0.85rem",
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
  },
  inlineLegend: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  inlineLegendItem: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
  },
  inlineLegendDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  chartBody: {
    flex: 1,
    minHeight: 0,
  },
  statRows: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: theme.spacing(1.5),
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: theme.spacing(0.5),
    ...theme.scrollbarStyles,
  },
  statRow: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  statRowHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    fontSize: "0.8rem",
  },
  statRowLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
  },
  statRowName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  statRowDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  statRowValue: {
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  statRowBar: {
    height: 6,
    borderRadius: 3,
    background: theme.palette.action.hover,
    overflow: "hidden",
  },
  statRowBarFill: {
    height: "100%",
    borderRadius: 3,
  },
  donutWrap: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  donutChart: {
    flex: 1,
    minHeight: 0,
    position: "relative",
  },
  donutCenter: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    pointerEvents: "none",
  },
  donutCenterValue: {
    fontSize: "1.3rem",
    fontWeight: 700,
    lineHeight: 1.1,
  },
  donutCenterLabel: {
    fontSize: "0.65rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  donutLegend: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme.spacing(1.5),
    flexShrink: 0,
  },
  placeholderBox: {
    flex: 1,
    minHeight: 0,
    borderRadius: 8,
    background: theme.palette.action.hover,
  },
  placeholderNum: {
    height: "1.4rem",
    width: 56,
    borderRadius: 4,
    background: theme.palette.action.hover,
  },
  emptyOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
  },
}));

const isoDate = (date) => date.toISOString().slice(0, 10);
const startOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const dayCount = (fromIso, toIso) => {
  const ms = new Date(toIso) - new Date(fromIso);
  return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)) + 1);
};

const CHART_COLORS = {
  success: "#4ade80",
  error: "#f87171",
};

const DURATION_SHADES = ["#ddd6fe", "#c4b5fd", "#a78bfa", "#8b5cf6", "#7c3aed"];

const SPECIALIST_SHADES = ["#4ade80", "#60a5fa", "#fbbf24", "#f472b6", "#34d399", "#a78bfa", "#f87171"];

const MONTH_SHORT_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const MONTH_SHORT_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDayTick = (isoStr) => {
  const [, m, d] = isoStr.split("-");
  const months = i18n.language?.startsWith("en") ? MONTH_SHORT_EN : MONTH_SHORT_ES;
  return `${Number(d)} ${months[Number(m) - 1]}`;
};

const formatRange = (fromIso, toIso) => {
  const [yFrom] = fromIso.split("-");
  const [yTo] = toIso.split("-");
  const from = formatDayTick(fromIso);
  const to = formatDayTick(toIso);
  return yFrom === yTo ? `${from} - ${to} ${yTo}` : `${from} ${yFrom} - ${to} ${yTo}`;
};

const StatRows = ({ classes, data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className={classes.statRows}>
      {data.map((item) => {
        const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
        return (
          <div key={item.name} className={classes.statRow}>
            <div className={classes.statRowHead}>
              <span className={classes.statRowLabel}>
                <span className={classes.statRowDot} style={{ background: item.color }} />
                <span className={classes.statRowName} title={item.name}>
                  {item.name}
                </span>
              </span>
              <span className={classes.statRowValue}>{pct}%</span>
            </div>
            <div className={classes.statRowBar}>
              <div className={classes.statRowBarFill} style={{ width: `${pct}%`, background: item.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const tooltipStyleProps = (theme) => ({
  contentStyle: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
    fontSize: 12,
    padding: "8px 12px",
  },
  labelStyle: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    marginBottom: 4,
  },
  itemStyle: {
    padding: 0,
  },
});

const DonutTooltip = ({ active, payload, theme }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;

  return (
    <div
      style={{
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
        fontSize: 12,
        padding: "8px 12px",
        color: item.color,
        fontWeight: 600,
      }}
    >
      {item.name} : {item.value}
    </div>
  );
};

const Donut = ({ classes, theme, data, center }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const topItem = data.reduce((max, item) => (item.value > (max?.value || 0) ? item : max), null);
  const centerValue = center?.value ?? (total > 0 && topItem ? `${Math.round((topItem.value / total) * 100)}%` : "—");
  const centerLabel = center?.label ?? (topItem?.name || "");

  return (
    <div className={classes.donutWrap}>
      <div className={classes.donutChart}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="62%"
              outerRadius="85%"
              paddingAngle={2}
              stroke="none"
            >
              {data.map((slice, i) => (
                <Cell key={`${slice.name}-${i}`} fill={slice.color} />
              ))}
            </Pie>
            <Tooltip content={<DonutTooltip theme={theme} />} />
          </PieChart>
        </ResponsiveContainer>
        <div className={classes.donutCenter}>
          <div className={classes.donutCenterValue}>{centerValue}</div>
          <div className={classes.donutCenterLabel}>{centerLabel}</div>
        </div>
      </div>
      <div className={classes.donutLegend}>
        {data.map((item) => (
          <span key={item.name} className={classes.inlineLegendItem}>
            <span className={classes.inlineLegendDot} style={{ background: item.color }} />
            {item.name}
          </span>
        ))}
      </div>
    </div>
  );
};

const buildStatTiles = () => [
  { key: "calls", label: i18n.t("callMetrics.tiles.calls"), format: (v) => v },
  {
    key: "successRate",
    label: i18n.t("callMetrics.tiles.successRate"),
    format: (v) => `${v}%`,
    tooltip: i18n.t("callMetrics.tooltips.successRate"),
  },
  {
    key: "resolvedRate",
    label: i18n.t("callMetrics.tiles.resolvedRate"),
    format: (v, m) => (v === null ? "—" : `${v}%`),
    tooltip: i18n.t("callMetrics.tooltips.resolvedRate"),
  },
  { key: "estimatedCostUsd", label: i18n.t("callMetrics.tiles.cost"), format: (v) => `$${v.toFixed(2)}` },
  { key: "knownContactRate", label: i18n.t("callMetrics.tiles.knownContacts"), format: (v) => `${v}%` },
];

const CallMetrics = () => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const STAT_TILES = buildStatTiles();
  const PERIOD_OPTIONS = buildPeriodOptions();

  const [period, setPeriod] = useState("mes");
  const [dateFrom, setDateFrom] = useState(isoDate(startOfMonth()));
  const [dateTo, setDateTo] = useState(isoDate(new Date()));
  const [draftRange, setDraftRange] = useState({ dateFrom, dateTo });

  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calls/metrics", { params: { dateFrom, dateTo } });
      setMetrics(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const applyRange = () => {
    setPeriod("personalizado");
    setDateFrom(draftRange.dateFrom);
    setDateTo(draftRange.dateTo);
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
    if (value === "personalizado") return;

    const range = rangoDePeriodo(value);
    const newFrom = range.from.format("YYYY-MM-DD");
    const newTo = range.to.format("YYYY-MM-DD");
    setDraftRange({ dateFrom: newFrom, dateTo: newTo });
    setDateFrom(newFrom);
    setDateTo(newTo);
  };

  const showSkeleton = loading || !metrics || metrics.calls === 0;

  const specialistData = (metrics?.bySpecialist || []).map((s, i) => ({
    name: s.label === NO_SPECIALIST_KEY ? i18n.t("callMetrics.noSpecialist") : s.label,
    value: s.calls,
    color: s.label === NO_SPECIALIST_KEY ? "#6b7280" : SPECIALIST_SHADES[i % SPECIALIST_SHADES.length],
  }));

  const sentimentData = (metrics?.bySentiment || [])
    .map((s) => ({
      level: s.sentiment,
      name: sentimentInfo(s.sentiment)?.label || s.sentiment,
      value: s.calls,
      color: sentimentInfo(s.sentiment)?.color || "#6b7280",
    }))
    .filter((s) => s.value > 0);

  const sentimentTotal = sentimentData.reduce((sum, s) => sum + s.value, 0);
  const satisfiedTotal = sentimentData
    .filter((s) => SATISFIED_LEVELS.includes(s.level))
    .reduce((sum, s) => sum + s.value, 0);
  const sentimentCenter = {
    value: sentimentTotal > 0 ? `${Math.round((satisfiedTotal / sentimentTotal) * 100)}%` : "—",
    label: i18n.t("callMetrics.satisfiedCenterLabel"),
  };

  const transferData = [
    { name: i18n.t("callMetrics.transferLabels.answered"), value: metrics?.transferSummary?.answered || 0, color: "#2fae60" },
    { name: i18n.t("callMetrics.transferLabels.notAnswered"), value: metrics?.transferSummary?.notAnswered || 0, color: "#e53935" },
  ].filter((s) => s.value > 0);

  const durationData = (metrics?.byDuration || [])
    .map((d, i) => ({
      name: d.label,
      value: d.calls,
      color: DURATION_SHADES[i % DURATION_SHADES.length],
    }))
    .filter((d) => d.value > 0);

  return (
    <MainContainer maxWidth={false}>
      <MainHeader>
        <Title>{i18n.t("callMetrics.title")}</Title>
        <div className={classes.dateFilterControl} style={{ marginLeft: "auto" }}>
          <PeriodSelect
            className={classes.periodSelectPill}
            value={period}
            onChange={handlePeriodChange}
            opciones={PERIOD_OPTIONS}
          />
          <input
            type="date"
            className={classes.dateFilterInput}
            value={draftRange.dateFrom}
            onChange={(e) => {
              setPeriod("personalizado");
              setDraftRange((r) => ({ ...r, dateFrom: e.target.value }));
            }}
          />
          <input
            type="date"
            className={classes.dateFilterInput}
            value={draftRange.dateTo}
            onChange={(e) => {
              setPeriod("personalizado");
              setDraftRange((r) => ({ ...r, dateTo: e.target.value }));
            }}
          />
          <Button
            variant="contained"
            className={classes.dateFilterButton}
            onClick={applyRange}
          >
            {i18n.t("callMetrics.apply")}
          </Button>
        </div>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.grid}>
          {STAT_TILES.map((tile) => (
            <div key={tile.key} className={classes.tile}>
              {showSkeleton ? (
                <div className={classes.placeholderNum} />
              ) : (
                <div className={classes.num}>{tile.format(metrics[tile.key], metrics)}</div>
              )}
              <div className={classes.label}>
                {tile.label}
                {tile.tooltip && (
                  <MuiTooltip title={tile.tooltip} placement="top" arrow>
                    <InfoOutlined className={classes.infoIcon} />
                  </MuiTooltip>
                )}
              </div>
              {!showSkeleton && tile.hint && (
                <div className={classes.tileHint}>{tile.hint(metrics)}</div>
              )}
            </div>
          ))}
        </div>

        <div className={classes.chartsRowFull}>
          <div className={cx(classes.chartCard, classes.chartCardTall)}>
            <div className={classes.sectionTitleRow}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                {i18n.t("callMetrics.callsPerDayTitle", {
                  range: formatRange(dateFrom, dateTo),
                  days: dayCount(dateFrom, dateTo),
                })}
              </Typography>
              <div className={classes.inlineLegend}>
                <span className={classes.inlineLegendItem}>
                  <span className={classes.inlineLegendDot} style={{ background: CHART_COLORS.success }} />
                  {i18n.t("callMetrics.legend.resolved")}
                </span>
                <span className={classes.inlineLegendItem}>
                  <span className={classes.inlineLegendDot} style={{ background: CHART_COLORS.error }} />
                  {i18n.t("callMetrics.legend.notResolved")}
                </span>
                <span className={classes.inlineLegendItem}>
                  <span className={classes.inlineLegendDot} style={{ background: "#6b7280" }} />
                  {i18n.t("callMetrics.legend.notAnalyzed")}
                </span>
              </div>
            </div>
            {showSkeleton ? (
              <div className={classes.placeholderBox} />
            ) : (
              <div className={classes.chartBody}>
                <ResponsiveContainer key={`by-day-${dateFrom}-${dateTo}`}>
                  <AreaChart data={metrics.byDay} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="areaSuccess" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.5} />
                        <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="areaError" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.error} stopOpacity={0.5} />
                        <stop offset="95%" stopColor={CHART_COLORS.error} stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="areaUnanalyzed" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6b7280" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#6b7280" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      stroke={theme.palette.text.disabled}
                      fontSize={11}
                      tickFormatter={formatDayTick}
                      interval="preserveStartEnd"
                      minTickGap={20}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke={theme.palette.text.disabled}
                      fontSize={11}
                      allowDecimals={false}
                      domain={[0, "dataMax"]}
                      tickLine={false}
                      axisLine={false}
                      width={28}
                    />
                    <Tooltip
                      labelFormatter={formatDayTick}
                      cursor={{ stroke: theme.palette.text.disabled, strokeDasharray: "3 3" }}
                      {...tooltipStyleProps(theme)}
                    />
                    <Area type="monotone" dataKey="resolvedCalls" name={i18n.t("callMetrics.legend.resolved")} stroke={CHART_COLORS.success} fill="url(#areaSuccess)" isAnimationActive={false} />
                    <Area type="monotone" dataKey="notResolvedCalls" name={i18n.t("callMetrics.legend.notResolved")} stroke={CHART_COLORS.error} fill="url(#areaError)" isAnimationActive={false} />
                    <Area type="monotone" dataKey="notAnalyzedCalls" name={i18n.t("callMetrics.legend.notAnalyzed")} stroke="#6b7280" fill="url(#areaUnanalyzed)" isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            {!loading && metrics && metrics.calls === 0 && (
              <div className={classes.emptyOverlay}>{i18n.t("callMetrics.emptyNoCallsInRange")}</div>
            )}
          </div>
        </div>

        <div className={classes.chartsRow}>
          <div className={classes.chartCard}>
            <Typography className={classes.sectionTitle}>
              {i18n.t("callMetrics.transfersTitle", { count: metrics?.transferSummary?.total || 0 })}
            </Typography>
            {showSkeleton ? (
              <div className={classes.placeholderBox} />
            ) : (
              <Donut classes={classes} theme={theme} data={transferData} />
            )}
            {!loading && metrics && metrics.calls > 0 && transferData.length === 0 && (
              <div className={classes.emptyOverlay}>{i18n.t("callMetrics.emptyNoTransfers")}</div>
            )}
            {!loading && metrics && metrics.calls === 0 && (
              <div className={classes.emptyOverlay}>{i18n.t("callMetrics.emptyNoData")}</div>
            )}
          </div>

          <div className={classes.chartCard}>
            <Typography className={classes.sectionTitle}>{i18n.t("callMetrics.sentimentTitle")}</Typography>
            {showSkeleton ? (
              <div className={classes.placeholderBox} />
            ) : (
              <Donut classes={classes} theme={theme} data={sentimentData} center={sentimentCenter} />
            )}
            {!loading && metrics && metrics.calls > 0 && sentimentData.length === 0 && (
              <div className={classes.emptyOverlay}>{i18n.t("callMetrics.emptyNoAnalyzed")}</div>
            )}
            {!loading && metrics && metrics.calls === 0 && (
              <div className={classes.emptyOverlay}>{i18n.t("callMetrics.emptyNoData")}</div>
            )}
          </div>

          <div className={classes.chartCard}>
            <Typography className={classes.sectionTitle}>{i18n.t("callMetrics.durationTitle")}</Typography>
            {showSkeleton ? (
              <div className={classes.placeholderBox} />
            ) : (
              <Donut classes={classes} theme={theme} data={durationData} />
            )}
            {!loading && metrics && metrics.calls === 0 && (
              <div className={classes.emptyOverlay}>{i18n.t("callMetrics.emptyNoData")}</div>
            )}
          </div>

          <div className={classes.chartCard}>
            <Typography className={classes.sectionTitle}>{i18n.t("callMetrics.specialistTitle")}</Typography>
            {showSkeleton ? (
              <div className={classes.placeholderBox} />
            ) : (
              <StatRows classes={classes} data={specialistData} />
            )}
            {!loading && metrics && metrics.calls === 0 && (
              <div className={classes.emptyOverlay}>{i18n.t("callMetrics.emptyNoData")}</div>
            )}
          </div>

        </div>
      </Paper>
    </MainContainer>
  );
};

export default CallMetrics;
